import { useState } from 'react';
import { format, startOfMonth } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Play, Loader2, AlertTriangle } from 'lucide-react';
import { DATE_PRESETS } from '../../utils/datePresets';
import type { AggregationRun, DatePreset } from '../../types/analytics';
import type { DateRange } from 'react-day-picker';

interface AggregationPanelProps {
  selectedPreset: DatePreset;
  onPresetChange: (preset: DatePreset) => void;
  customDateFrom: string;
  customDateTo: string;
  onCustomDateChange: (dateFrom: string, dateTo: string) => void;
  onTriggerAggregation: () => void;
  isRunning: boolean;
  isPending: boolean;
  lastRun: AggregationRun | null;
}

export default function AggregationPanel({
  selectedPreset,
  onPresetChange,
  customDateFrom,
  customDateTo,
  onCustomDateChange,
  onTriggerAggregation,
  isRunning,
  isPending,
  lastRun,
}: AggregationPanelProps) {
  const [calendarRange, setCalendarRange] = useState<DateRange | undefined>({
    from: new Date(customDateFrom),
    to: new Date(customDateTo),
  });

  const handleSelect = (range: DateRange | undefined) => {
    setCalendarRange(range);
    if (range?.from && range?.to) {
      onCustomDateChange(
        format(range.from, 'yyyy-MM-dd'),
        format(range.to, 'yyyy-MM-dd'),
      );
    }
  };

  const isStale = lastRun
    ? Date.now() - new Date(lastRun.completedAt || lastRun.startedAt).getTime() > 24 * 60 * 60 * 1000
    : true;

  return (
    <div className="flex gap-4 items-start">
      <Card className="bg-admin-dark-800 border-admin-slate-300/10 flex-1">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.value}
                onClick={() => onPresetChange(preset.value)}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  selectedPreset === preset.value
                    ? 'bg-admin-accent-blue text-white border-admin-accent-blue'
                    : 'bg-admin-dark-700 text-admin-slate-400 border-admin-slate-300/20 hover:bg-admin-dark-600'
                }`}
              >
                {preset.label}
              </button>
            ))}

            {selectedPreset === 'custom' && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="bg-admin-dark-700 border-admin-slate-300/20 text-admin-slate-400 hover:bg-admin-dark-600">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {customDateFrom} → {customDateTo}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-admin-dark-800 border-admin-slate-300/20" align="start">
                  <Calendar
                    mode="range"
                    selected={calendarRange}
                    onSelect={handleSelect}
                    numberOfMonths={2}
                    defaultMonth={startOfMonth(new Date())}
                    disabled={{ after: new Date() }}
                  />
                </PopoverContent>
              </Popover>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-admin-dark-800 border-admin-slate-300/10 shrink-0">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={onTriggerAggregation}
              disabled={isRunning || isPending}
              className="bg-admin-accent-blue hover:bg-blue-600"
            >
              {isRunning || isPending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running...</>
              ) : (
                <><Play className="w-4 h-4 mr-2" /> Run Aggregation</>
              )}
            </Button>

            {lastRun && (
              <span className="text-xs text-admin-slate-500">
                Last run: {lastRun.status === 'completed' ? '✓' : '✗'}{' '}
                {new Date(lastRun.completedAt || lastRun.startedAt).toLocaleString()}
                {lastRun.durationMs && ` (${lastRun.durationMs}ms)`}
              </span>
            )}

            {isStale && (
              <span className="flex items-center gap-1 text-xs text-admin-accent-yellow">
                <AlertTriangle className="w-3 h-3" /> Data may be stale
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
